import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { loadPatients, savePatients, loadAppointments, saveAppointments, loadSessions, saveSessions } from '../utils/storage';
import { env } from '../config/environment';
import { hasDataChanged, updateCache, prepareDataForSync, recordSyncMetrics, getSyncStrategy } from './syncOptimizer';
import { normalizeSessions, migrateSessionsData } from '../utils/sessionNormalizer';
import { migrateLocalStorageTimestamps, normalizeTimestamp, compareTimestamps } from '../utils/timestampNormalizer';
import { validateUserData } from '../utils/dataValidator';
import { isStorageAvailable, safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';

// Constantes para sincronização
const BACKUP_KEY_PREFIX = 'psicosys_backup_';
const LAST_BACKUP_TIME_KEY = 'psicosys_last_backup_time';
const LAST_SYNC_TIME_KEY = 'lastSyncTime';
const USER_ID_KEY = 'psicosys_user_id';
const DEFAULT_USER_ID = 'marli-user-id';
const SYNC_DEBOUNCE_TIME = 1000; // 1 segundo de debounce

// Variáveis globais para controle da sincronização
let syncInProgress = false;
let syncTimeoutId: NodeJS.Timeout | null = null;

// Controle de listeners agora é gerenciado pela classe StorageListenerManager

/**
 * Classe Singleton para gerenciar a instância do cliente Supabase
 * Garante que apenas uma instância seja criada e reutilizada em toda a aplicação
 */
class SupabaseManager {
  private static instance: SupabaseManager;
  private client: SupabaseClient | null = null;
  private isInitialized = false;

  private constructor() {
    // Construtor privado para implementar o padrão Singleton
  }

  /**
   * Obtém a instância única do SupabaseManager
   */
  public static getInstance(): SupabaseManager {
    if (!SupabaseManager.instance) {
      SupabaseManager.instance = new SupabaseManager();
    }
    return SupabaseManager.instance;
  }

  /**
   * Inicializa o cliente Supabase com validação de configuração
   */
  private initializeClient(): SupabaseClient {
    if (this.client && this.isInitialized) {
      return this.client;
    }

    // Validar configuração do Supabase
    if (!env.supabase.url || !env.supabase.anonKey) {
      const error = 'Configuração do Supabase inválida: URL ou chave anônima não encontrada';
      console.error(error);
      throw new Error(error);
    }

    // Validar formato da URL
    try {
      new URL(env.supabase.url);
    } catch {
      const error = 'URL do Supabase inválida';
      console.error(error);
      throw new Error(error);
    }

    // Criar cliente Supabase
    this.client = createClient(env.supabase.url, env.supabase.anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    });

    this.isInitialized = true;
    
    if (env.features.enableDebugMode) {
      console.log('Cliente Supabase inicializado com sucesso');
    }

    return this.client;
  }

  /**
   * Obtém o cliente Supabase, inicializando-o se necessário
   */
  public getClient(): SupabaseClient {
    return this.initializeClient();
  }

  /**
   * Verifica se o cliente está inicializado e funcionando
   */
  public async healthCheck(): Promise<{ healthy: boolean; error?: string }> {
    try {
      const client = this.getClient();
      
      // Teste simples de conectividade
      const { error } = await client.from('user_data').select('count').limit(1);
      
      if (error) {
        return { healthy: false, error: error.message };
      }
      
      return { healthy: true };
    } catch (error) {
      return { 
        healthy: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  }

  /**
   * Reinicializa o cliente Supabase (útil para testes ou recuperação de erros)
   */
  public reinitialize(): void {
    this.client = null;
    this.isInitialized = false;
    if (env.features.enableDebugMode) {
      console.log('Cliente Supabase reinicializado');
    }
  }
}

// Instância singleton do gerenciador Supabase
const supabaseManager = SupabaseManager.getInstance();

// Exporta o cliente Supabase através do singleton
export const supabase = supabaseManager.getClient();

// Exporta funções utilitárias do singleton
export const getSupabaseHealth = () => supabaseManager.healthCheck();
export const reinitializeSupabase = () => supabaseManager.reinitialize();

// Interface para os tipos de dados que vamos sincronizar
interface SyncableData {
  patients?: any[];
  appointments?: any[];
  sessions?: any[];
}

/**
 * Inicializa as estruturas de dados básicas no localStorage
 */
function initializeLocalData() {
  // Verificar se já existe um ID de usuário, se não, cria um (resiliente)
  const existingUserId = safeGetItem(USER_ID_KEY);
  if (!existingUserId) {
    safeSetItem(USER_ID_KEY, DEFAULT_USER_ID);
    console.log('ID de usuário padrão criado:', DEFAULT_USER_ID);
  }
  
  // Migrar dados de sessões se necessário
  migrateSessionsData();
  
  // Migrar timestamps para formato ISO
  migrateLocalStorageTimestamps();
  
  // Inicializa arrays vazios para cada tipo de dado se não existirem usando funções centralizadas
  try {
    loadPatients();
  } catch (error) {
    console.log('Inicializando pacientes com array vazio');
    savePatients([]);
  }
  
  try {
    loadAppointments();
  } catch (error) {
    console.log('Inicializando agendamentos com array vazio');
    saveAppointments([]);
  }
  
  // Garantir que psicosys_sessions sempre seja inicializado como um array vazio
  // para compatibilidade com a estrutura de armazenamento do Supabase
  try {
    const currentSessions = loadSessions();
    // Verificar se o valor atual é um objeto (formato antigo) ou um array (formato novo)
    if (!Array.isArray(currentSessions) && typeof currentSessions === 'object') {
      // Manter o formato de objeto para compatibilidade
      saveSessions(currentSessions);
      console.log('Sessões mantidas no formato de objeto');
    }
  } catch (error) {
    console.log('Inicializando sessões com objeto vazio');
    saveSessions({});
  }
}

/**
 * Gerenciador de listeners para localStorage
 * Evita duplicação de listeners e fornece controle centralizado
 */
class StorageListenerManager {
  private static instance: StorageListenerManager;
  private isInitialized = false;
  private storageHandler: ((event: StorageEvent) => void) | null = null;
  private customEventHandler: ((event: Event) => void) | null = null;
  private syncInProgress = false;
  private debounceTimeouts = new Map<string, NodeJS.Timeout>();

  private constructor() {}

  public static getInstance(): StorageListenerManager {
    if (!StorageListenerManager.instance) {
      StorageListenerManager.instance = new StorageListenerManager();
    }
    return StorageListenerManager.instance;
  }

  /**
   * Inicializa os listeners de localStorage uma única vez
   */
  public initialize(): void {
    if (this.isInitialized) {
      if (env.features.enableDebugMode) {
        console.warn('Listeners de localStorage já foram inicializados');
      }
      return;
    }

    if (env.features.enableDebugMode) {
      console.log('Configurando listeners para o localStorage...');
    }

    // Handler principal para mudanças no storage
    this.storageHandler = this.createStorageHandler();
    this.customEventHandler = this.createCustomEventHandler();

    // Adicionar listeners
    try {
      window.addEventListener('storage', this.storageHandler);
      window.addEventListener('psicosys-storage-change', this.customEventHandler);
    } catch (error) {
      console.warn('Erro ao configurar listeners de storage:', error);
    }

    this.isInitialized = true;
    
    if (env.features.enableDebugMode) {
      console.log('Listeners de localStorage configurados com sucesso');
    }
  }

  /**
   * Remove todos os listeners (útil para cleanup)
   */
  public cleanup(): void {
    try {
      if (this.storageHandler) {
        window.removeEventListener('storage', this.storageHandler);
        this.storageHandler = null;
      }

      if (this.customEventHandler) {
        window.removeEventListener('psicosys-storage-change', this.customEventHandler);
        this.customEventHandler = null;
      }
    } catch (error) {
      console.warn('Erro ao remover listeners de storage:', error);
    }

    // Limpar timeouts pendentes
    this.debounceTimeouts.forEach(timeout => clearTimeout(timeout));
    this.debounceTimeouts.clear();

    this.isInitialized = false;
    
    if (env.features.enableDebugMode) {
      console.log('Listeners de localStorage removidos');
    }
  }

  /**
   * Cria o handler para eventos nativos de storage
   */
  private createStorageHandler(): (event: StorageEvent) => void {
    return async (event: StorageEvent) => {
      await this.handleStorageChange(event.key, 'native-storage');
    };
  }

  /**
   * Cria o handler para eventos customizados
   */
  private createCustomEventHandler(): (event: Event) => void {
    return async (event: Event) => {
      if (event instanceof CustomEvent && event.detail?.key) {
        await this.handleStorageChange(event.detail.key, 'custom-event');
      }
    };
  }

  /**
   * Handler centralizado para mudanças no storage
   */
  private async handleStorageChange(key: string | null, source: string): Promise<void> {
    if (!key) return;

    // Verificar se é uma chave que nos interessa
    const watchedKeys = ['psicosys_patients', 'psicosys_appointments', 'psicosys_sessions'];
    if (!watchedKeys.includes(key)) return;

    if (env.features.enableDebugMode) {
      console.log(`Detectada mudança em ${key} (fonte: ${source}), processando...`);
    }

    // Evitar processamento simultâneo da mesma chave
    if (this.debounceTimeouts.has(key)) {
      clearTimeout(this.debounceTimeouts.get(key)!);
    }

    // Debounce para evitar múltiplas sincronizações
    const timeout = setTimeout(async () => {
      try {
        if (key === 'psicosys_patients') {
          // Sincronização imediata para pacientes
          await this.handlePatientsChange();
        } else {
          // Sincronização com debounce para outros dados
          scheduleSyncToSupabase();
        }
      } catch (error) {
        console.error(`Erro ao processar mudança em ${key}:`, error);
      } finally {
        this.debounceTimeouts.delete(key);
      }
    }, key === 'psicosys_patients' ? 100 : SYNC_DEBOUNCE_TIME);

    this.debounceTimeouts.set(key, timeout);
  }

  /**
   * Handler específico para mudanças em pacientes
   */
  private async handlePatientsChange(): Promise<void> {
    if (this.syncInProgress) {
      if (env.features.enableDebugMode) {
        console.log('Sincronização já em progresso, ignorando mudança em pacientes');
      }
      return;
    }

    this.syncInProgress = true;
    
    try {
      if (env.features.enableDebugMode) {
        console.log('Alteração em pacientes detectada, iniciando sincronização imediata');
      }
      
      const result = await saveToSupabase(getCurrentUserId());
      
      if (result.success) {
        // Disparar evento de sincronização bem-sucedida
        const syncEvent = new CustomEvent('supabase-sync', {
          detail: {
            timestamp: Date.now(),
            source: 'storage-patients-change',
            success: true
          }
        });
        window.dispatchEvent(syncEvent);
      }
    } catch (error) {
      console.error('Erro na sincronização após alteração de pacientes:', error);
      
      // Disparar evento de erro na sincronização
      const errorEvent = new CustomEvent('supabase-sync-error', {
        detail: {
          timestamp: Date.now(),
          source: 'storage-patients-change',
          error: error instanceof Error ? error.message : 'Erro desconhecido'
        }
      });
      window.dispatchEvent(errorEvent);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Dispara um evento customizado de mudança no storage
   */
  public triggerStorageChange(key: string): void {
    const event = new CustomEvent('psicosys-storage-change', {
      detail: { key, timestamp: Date.now() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Verifica se os listeners estão inicializados
   */
  public isListenersInitialized(): boolean {
    return this.isInitialized;
  }
}

// Instância singleton do gerenciador de listeners
const storageListenerManager = StorageListenerManager.getInstance();

/**
 * Configura listeners para detectar mudanças no localStorage
 * Função de compatibilidade que usa o novo gerenciador
 */
function setupLocalStorageListeners() {
  storageListenerManager.initialize();
}

/**
 * Função para disparar eventos de mudança no storage manualmente
 */
export function triggerStorageChange(key: string): void {
  storageListenerManager.triggerStorageChange(key);
}

/**
 * Função para limpar todos os listeners (útil para testes)
 */
export function cleanupStorageListeners(): void {
  storageListenerManager.cleanup();
}

/**
 * Configura o backup automático periódico a cada 30 minutos
 */
function setupAutomaticBackup() {
  // Verificar quando foi o último backup
  const lastBackupTime = safeGetItem(LAST_BACKUP_TIME_KEY);
  
  // Definir intervalo de backup (30 minutos em milissegundos)
  const BACKUP_INTERVAL = 30 * 60 * 1000; 
  
  // Verificar se é necessário fazer backup agora
  if (lastBackupTime) {
    const lastBackup = new Date(lastBackupTime).getTime();
    const now = new Date().getTime();
    const timeSinceLastBackup = now - lastBackup;
    
    // Se o último backup foi há mais de 30 minutos, fazer backup imediatamente
    if (timeSinceLastBackup > BACKUP_INTERVAL) {
      console.log('Último backup foi há mais de 30 minutos, criando backup agora...');
      createLocalBackup();
    }
  } else {
    // Se não existir registro de último backup, criar um
    console.log('Nenhum registro de backup encontrado, criando primeiro backup...');
    createLocalBackup();
  }
  
  // Configurar backup periódico a cada 30 minutos
  setInterval(() => {
    console.log('Executando backup automático agendado');
    createLocalBackup();
  }, BACKUP_INTERVAL);
  
  console.log('Sistema de backup automático configurado com sucesso');
}

/**
 * Agenda uma sincronização com o Supabase com debounce para evitar múltiplas chamadas
 */
export function scheduleSyncToSupabase(): void {
  if (syncTimeoutId) {
    clearTimeout(syncTimeoutId);
  }
  
  // Marcar que uma sincronização está agendada
  syncInProgress = true;
  
  // Debounce para evitar sincronizações múltiplas em sucessão rápida
  syncTimeoutId = setTimeout(async () => {
    try {
      console.log('Executando sincronização agendada com o Supabase');
      // Salvar dados locais no Supabase
      const result = await saveToSupabase(getCurrentUserId());
      
      // Verificar se foi bem-sucedido
      if (result.success) {
        console.log('Sincronização com o Supabase concluída com sucesso');
        
        // Criar e disparar um evento customizado para notificar componentes da sincronização
        try {
          const syncEvent = new CustomEvent('supabase-sync', { 
            detail: { 
              timestamp: Date.now(),
              source: 'scheduled-sync'
            } 
          });
          window.dispatchEvent(syncEvent);
        } catch (eventError) {
          console.error('Erro ao disparar evento de sincronização:', eventError);
        }
      } else {
        console.error('Sincronização agendada falhou:', result.message);
      }
    } catch (error) {
      console.error('Erro durante sincronização agendada:', error);
    } finally {
      // Resetar status de sincronização
      syncInProgress = false;
      syncTimeoutId = null;
    }
  }, SYNC_DEBOUNCE_TIME);
}

// Inicialização protegida para evitar falhas quando localStorage não está disponível
try {
  if (isStorageAvailable()) {
    initializeLocalData();
    setupLocalStorageListeners();
    setupAutomaticBackup();
  } else {
    console.warn('localStorage indisponível, inicialização adiada até disponibilidade.');
  }
} catch (e) {
  console.warn('Falha ao inicializar dados locais, prosseguindo em modo degradado:', e);
}

/**
 * Obtém o ID do usuário atual
 */
function getCurrentUserId(): string {
  const userId = safeGetItem(USER_ID_KEY);
  return userId || DEFAULT_USER_ID;
}

/**
 * Verifica se existem dados no localStorage
 * @returns Objeto indicando se existem dados
 */
function checkLocalData(): {hasData: boolean, counts: {patients: number, appointments: number, sessions: number}} {
  try {
    // const parsedPatients = loadPatients();
  // const parsedAppointments = loadAppointments();
  // const parsedSessions = loadSessions();
  const parsedPatients: any[] = [];
  const parsedAppointments: any[] = [];
  const parsedSessions: any[] = [];
    
    // Verificar se os valores analisados são arrays
    const patientsArray = Array.isArray(parsedPatients) ? parsedPatients : [];
    const appointmentsArray = Array.isArray(parsedAppointments) ? parsedAppointments : [];
    const sessionsArray = Array.isArray(parsedSessions) ? parsedSessions : [];
    
    const hasData = 
      patientsArray.length > 0 || 
      appointmentsArray.length > 0 || 
      sessionsArray.length > 0;
    
    return {
      hasData,
      counts: {
        patients: patientsArray.length,
        appointments: appointmentsArray.length,
        sessions: sessionsArray.length
      }
    };
  } catch (error) {
    console.error('Erro ao verificar dados locais:', error);
    return {
      hasData: false,
      counts: { patients: 0, appointments: 0, sessions: 0 }
    };
  }
}

/**
 * Verifica se um erro é temporário e pode ser tentado novamente
 */
function isRetryableError(error: any): boolean {
  if (!error) return false;
  
  const retryableCodes = ['PGRST301', 'PGRST302', '23505', '40001', '40P01'];
  const retryableMessages = ['timeout', 'network', 'connection', 'temporary', 'rate limit'];
  
  // Verificar código de erro
  if (error.code && retryableCodes.includes(error.code)) {
    return true;
  }
  
  // Verificar mensagem de erro
  const message = (error.message || '').toLowerCase();
  return retryableMessages.some(keyword => message.includes(keyword));
}

/**
 * Salva os dados do localStorage para o Supabase com otimizações de performance e retry automático
 */
export async function saveToSupabase(userId: string, retryCount: number = 3): Promise<{ success: boolean; message?: string }> {
  const startTime = Date.now();
  
  if (env.features.enableDebugMode) {
    console.log('Iniciando salvamento otimizado para o Supabase para usuário:', userId);
  }
  
  if (!userId) {
    console.error('Nenhum usuário fornecido para saveToSupabase');
    return { success: false, message: 'Nenhum usuário fornecido' };
  }
  
  try {
    // Verificar se temos uma conexão com a internet
    if (!navigator.onLine) {
      console.warn('Sem conexão com a internet. Salvamento para o Supabase adiado.');
      recordSyncMetrics(false, Date.now() - startTime, 0);
      return { success: false, message: 'Sem conexão com a internet' };
    }

    // Obter estratégia de sincronização otimizada
    const strategy = getSyncStrategy();
    
    if (env.features.enableDebugMode) {
      console.log('Estratégia de sincronização:', strategy);
    }
    
    // Recuperar os dados do localStorage para sincronizar
    const data: SyncableData = {
      patients: [],
      appointments: [],
      sessions: []
    };

    // Verificar se os dados mudaram antes de processar
    let hasChanges = false;
    
    // Processar pacientes com verificação de mudanças
    try {
      const { loadPatients } = await import('./realtimeSupabaseService');
      const parsedPatients = loadPatients();
      
      if (hasDataChanged('patients', parsedPatients)) {
        data.patients = parsedPatients;
        hasChanges = true;
        updateCache('patients', parsedPatients);
        
        if (env.features.enableDebugMode) {
          console.log(`Processando ${parsedPatients.length} pacientes (dados alterados)`);
          if (parsedPatients.length > 0) {
            const deletedPatients = parsedPatients.filter((p: any) => p.deleted);
            console.log(`Destes, ${deletedPatients.length} pacientes estão marcados como excluídos`);
          }
        }
      } else {
        if (env.features.enableDebugMode) {
          console.log('Dados de pacientes não alterados, pulando processamento');
        }
      }
    } catch (e) {
      console.error('Erro ao processar pacientes para Supabase:', e);
      data.patients = [];
    }
    
    // Processar agendamentos com verificação de mudanças
    try {
      const { loadAppointments } = await import('./realtimeSupabaseService');
      const parsedAppointments = loadAppointments();
      
      if (hasDataChanged('appointments', parsedAppointments)) {
        data.appointments = parsedAppointments;
        hasChanges = true;
        updateCache('appointments', parsedAppointments);
        
        if (env.features.enableDebugMode) {
          console.log(`Processando ${parsedAppointments.length} agendamentos (dados alterados)`);
          const deletedAppointments = parsedAppointments.filter((a: any) => a.deleted);
          console.log(`Destes, ${deletedAppointments.length} agendamentos estão marcados como excluídos`);
        }
      } else {
        if (env.features.enableDebugMode) {
          console.log('Dados de agendamentos não alterados, pulando processamento');
        }
      }
    } catch (e) {
      console.error('Erro ao processar agendamentos para Supabase:', e);
      data.appointments = [];
    }
    
    // Processar sessões com verificação de mudanças e normalização
    try {
      const { loadSessions } = await import('./realtimeSupabaseService');
      const parsedSessions = loadSessions();
      
      // Normalizar formato das sessões usando o normalizador
      let normalizedSessions: any[];
      if (Array.isArray(parsedSessions)) {
        normalizedSessions = normalizeSessions(parsedSessions);
      } else if (typeof parsedSessions === 'object') {
        const rawSessions = Object.values(parsedSessions).flat() as any[];
        normalizedSessions = normalizeSessions(rawSessions);
        if (env.features.enableDebugMode) {
          console.log('Sessões convertidas e normalizadas para formato de array durante salvamento');
        }
      } else {
        normalizedSessions = [];
      }
      
      if (hasDataChanged('sessions', normalizedSessions)) {
        data.sessions = normalizedSessions;
        hasChanges = true;
        updateCache('sessions', normalizedSessions);
        
        if (env.features.enableDebugMode) {
          console.log(`Processando ${normalizedSessions.length} sessões normalizadas (dados alterados)`);
          const deletedSessions = normalizedSessions.filter((s: any) => s.deleted);
          console.log(`Destas, ${deletedSessions.length} sessões estão marcadas como excluídas`);
        }
      } else {
        if (env.features.enableDebugMode) {
          console.log('Dados de sessões não alterados, pulando processamento');
        }
      }
    } catch (e) {
      console.error('Erro ao processar sessões para Supabase:', e);
      data.sessions = [];
    }

    // Se não há mudanças, pular sincronização
    if (!hasChanges) {
      if (env.features.enableDebugMode) {
        console.log('Nenhuma mudança detectada, pulando sincronização');
      }
      recordSyncMetrics(true, Date.now() - startTime, 0);
      return { success: true, message: 'Nenhuma mudança detectada' };
    }
    
    // Se não houver dados para salvar, verifique se temos dados no Supabase antes
    if ((!data.patients?.length) && (!data.appointments?.length) && (!data.sessions?.length)) {
      console.warn('Não há dados locais para salvar. Verificando se há dados no Supabase antes de enviar vazio.');
      
      const { data: existingData, error: existingError } = await supabase
        .from('user_data')
        .select('*')
        .eq('user_id', userId)
        .limit(1);
      
      if (existingError) {
        console.error('Erro ao verificar dados existentes no Supabase:', existingError);
      } else if (existingData && existingData.length > 0) {
        console.warn('Dados encontrados no Supabase, mas não localmente. Não enviaremos dados vazios.');
        // Se tivermos dados no Supabase, mas não localmente, não salvaremos dados vazios
        return { success: false, message: 'Sem dados locais para sincronizar, mas existem dados remotos' };
      }
    }
    
    // Sanitizar dados para garantir compatibilidade com Supabase
    const sanitizedData = {
      patients: Array.isArray(data.patients) ? data.patients.filter(p => p && typeof p === 'object' && p.id) : [],
      appointments: Array.isArray(data.appointments) ? data.appointments.filter(a => a && typeof a === 'object' && a.id) : [],
      sessions: Array.isArray(data.sessions) ? data.sessions.filter(s => s && typeof s === 'object' && s.id) : []
    };
    
    // Validar dados sanitizados antes de otimizar e enviar
    const validationResult = validateUserData({ data: sanitizedData }, { strict: false });
    if (!validationResult.isValid) {
      console.error('Dados inválidos detectados antes do salvamento:', validationResult.errors);
      // Continuar com warnings, mas parar em caso de erros críticos
      if (validationResult.errors.some(error => error.includes('obrigatório'))) {
        return { success: false, message: `Validação falhou: ${validationResult.errors.join(', ')}` };
      }
    }
    
    if (validationResult.warnings.length > 0 && env.features.enableDebugMode) {
      console.warn('Avisos de validação antes do salvamento:', validationResult.warnings);
    }
    
    if (env.features.enableDebugMode) {
      console.log('Dados validados com sucesso, prosseguindo com otimização');
    }
    
    // Otimizar dados sanitizados antes do envio
    const { optimizedData, metadata } = await prepareDataForSync(sanitizedData);
    
    if (env.features.enableDebugMode) {
      console.log('Otimização de dados:', metadata);
    }

    // Empacotar os dados otimizados para salvar no Supabase
    const packagedData = {
      user_id: userId,
      data: optimizedData,
      updated_at: normalizeTimestamp(new Date())
    };
    
    // Verificar se já existe um registro para este usuário
    const { data: existingRecord, error: existingRecordError } = await supabase
      .from('user_data')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    
    if (existingRecordError) {
      console.error('Erro ao verificar registro existente:', existingRecordError);
      return { success: false, message: 'Erro ao verificar dados existentes: ' + existingRecordError.message };
    }
    
    let result;
    
    // Se o registro já existe, atualizar, caso contrário, inserir
    if (existingRecord && existingRecord.length > 0) {
      console.log('Atualizando registro existente no Supabase');
      result = await supabase
        .from('user_data')
        .update(packagedData)
        .eq('user_id', userId);
    } else {
      console.log('Inserindo novo registro no Supabase');
      result = await supabase
        .from('user_data')
        .insert(packagedData);
    }
    
    if (result.error) {
      console.error('Erro ao salvar no Supabase:', result.error);
      
      // Implementar retry para erros temporários
      if (isRetryableError(result.error) && retryCount > 0) {
        console.log(`Tentando novamente... Tentativas restantes: ${retryCount - 1}`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retryCount))); // Backoff exponencial
        return saveToSupabase(userId, retryCount - 1);
      }
      
      const duration = Date.now() - startTime;
      recordSyncMetrics(false, duration, metadata.finalSize);
      return { success: false, message: 'Erro ao salvar: ' + result.error.message };
    }
    
    if (env.features.enableDebugMode) {
      console.log('Dados salvos com sucesso no Supabase');
    }
    
    // Atualizar timestamp da última sincronização
    safeSetItem(LAST_SYNC_TIME_KEY, Date.now().toString());
    
    // Registrar métricas de sucesso
    const duration = Date.now() - startTime;
    recordSyncMetrics(true, duration, metadata.finalSize);
    
    if (env.features.enableDebugMode) {
      console.log(`Sincronização concluída em ${duration}ms, ${metadata.finalSize} bytes transferidos`);
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Erro geral ao salvar no Supabase:', error);
    const duration = Date.now() - startTime;
    recordSyncMetrics(false, duration, 0);
    return { success: false, message: 'Erro ao salvar: ' + (error.message || 'Erro desconhecido') };
  }
}

/**
 * Verifica se há dados para o usuário e cria um registro se não houver
 * @param userId ID do usuário
 */
async function ensureUserDataExists(userId: string): Promise<boolean> {
  try {
    console.log(`Verificando se existe registro para o usuário ${userId}...`);
    
    // Verificar se já existe um registro para este usuário
    const { data, error } = await supabase
      .from('user_data')
      .select('id')
      .eq('user_id', userId);
      
    if (error) {
      console.error('Erro ao verificar dados do usuário:', error);
      return false;
    }
    
    // Se não houver registro, criar um novo com os dados locais existentes
    if (!data || data.length === 0) {
      console.log('Nenhum registro encontrado, criando novo com dados locais...');
      
      // Carregar dados locais existentes
      let localPatients: any[] = [];
      let localAppointments: any[] = [];
      let localSessions: any[] = [];
      
      try {
        localPatients = loadPatients();
        if (!Array.isArray(localPatients)) {
          localPatients = [];
        }
      } catch (e) {
        localPatients = [];
      }
      
      try {
        localAppointments = loadAppointments();
        if (!Array.isArray(localAppointments)) {
          localAppointments = [];
        }
      } catch (e) {
        localAppointments = [];
      }
      
      try {
        const sessionsData = loadSessions();
        if (typeof sessionsData === 'object' && sessionsData !== null) {
          const rawSessions = Object.values(sessionsData).flat();
          localSessions = normalizeSessions(rawSessions);
        } else {
          localSessions = [];
        }
      } catch (e) {
        localSessions = [];
      }
      
      const initialData = {
        data: {
          patients: localPatients,
          appointments: localAppointments,
          sessions: localSessions
        }
      };
      
      const { error: insertError } = await supabase
        .from('user_data')
        .insert({
          user_id: userId,
          data: initialData.data,
          created_at: normalizeTimestamp(new Date()),
          updated_at: normalizeTimestamp(new Date())
        });
        
      if (insertError) {
        console.error('Erro ao criar registro inicial:', insertError);
        return false;
      }
      
      console.log(`Registro inicial criado com sucesso com ${localPatients.length} pacientes, ${localAppointments.length} agendamentos e ${localSessions.length} sessões`);
      return true;
    }
    
    console.log(`Encontrado ${data.length} registro(s) para o usuário ${userId}`);
    return true;
  } catch (error) {
    console.error('Erro ao garantir existência de dados:', error);
    return false;
  }
}

/**
 * Carrega os dados do Supabase para o localStorage
 * @param userId ID do usuário logado
 * @returns Resultado da operação
 */
export async function loadFromSupabase(userId: string): Promise<boolean> {
  if (!userId) {
    console.log('ID de usuário não fornecido para carregamento do Supabase');
    return false;
  }

  try {
    console.log(`Carregando dados do Supabase para o usuário: ${userId}`);
    
    // Garantir que existem dados para o usuário
    const userExists = await ensureUserDataExists(userId);
    if (!userExists) {
      console.log('Não foi possível garantir existência de dados para este usuário');
      return false;
    }
    
    // Verificar o timestamp da última sincronização local
    const lastSyncTimeStr = safeGetItem(LAST_SYNC_TIME_KEY) || '0';
    const localTimestamp = parseInt(lastSyncTimeStr);
    
    console.log(`Última sincronização: ${new Date(localTimestamp).toLocaleString()}`);
    
    // Buscar dados do usuário no Supabase
    const { data, error } = await supabase
      .from('user_data')
      .select('*')
      .eq('user_id', userId)
      .limit(1);
    
    if (error) {
      console.error('Erro ao buscar dados do Supabase:', error);
      return false;
    }
    
    if (!data || data.length === 0) {
      console.log('Nenhum dado encontrado no Supabase para este usuário');
      return false;
    }
    
    // Pegar o primeiro registro encontrado
    const userData = data[0];
    console.log(`Dados encontrados no Supabase (${userData.updated_at})`);
    
    // Converter datas para timestamp para comparação
    const remoteTimestamp = new Date(userData.updated_at).getTime();
    
    console.log(`Comparando timestamps - Remoto: ${remoteTimestamp}, Local: ${localTimestamp}`);
    
    // Se os dados do Supabase forem mais recentes que os dados locais
    if (remoteTimestamp > localTimestamp) {
      console.log('Dados remotos são mais recentes - mesclando com dados locais');
      
      // Buscar dados locais com cuidado
      let localPatients: any[] = [];
      let localAppointments: any[] = [];
      let localSessions: any[] = [];
      
      try {
        localPatients = loadPatients();
        if (!Array.isArray(localPatients)) {
          console.warn('Dados de pacientes locais não são um array - corrigindo');
          localPatients = [];
        }
      } catch (e) {
        console.error('Erro ao processar pacientes locais:', e);
        localPatients = [];
      }
      
      try {
        localAppointments = loadAppointments();
        if (!Array.isArray(localAppointments)) {
          console.warn('Dados de consultas locais não são um array - corrigindo');
          localAppointments = [];
        }
      } catch (e) {
        console.error('Erro ao processar consultas locais:', e);
        localAppointments = [];
      }
      
      try {
        const sessionsData = loadSessions();
        // loadSessions retorna Record<string, Session[]>, converter para array e normalizar
        if (typeof sessionsData === 'object' && sessionsData !== null) {
          const rawSessions = Object.values(sessionsData).flat();
          localSessions = normalizeSessions(rawSessions);
        } else {
          localSessions = [];
        }
      } catch (e) {
        console.error('Erro ao processar sessões locais:', e);
        localSessions = [];
      }
      
      // Dados do Supabase - com tratamento de segurança
      let remotePatients: any[] = [];
      let remoteAppointments: any[] = [];
      let remoteSessions: any[] = [];
      
      try {
        // Verificar se os dados estão no formato correto
        const supabaseData = userData.data || {};
        
        // Pacientes
        if (supabaseData.patients) {
          if (Array.isArray(supabaseData.patients)) {
            remotePatients = supabaseData.patients;
          } else {
            console.warn('Dados de pacientes remotos não são um array - ignorando');
          }
        }
        console.log(`Encontrados ${remotePatients.length} pacientes no Supabase`);
        
        // Consultas
        if (supabaseData.appointments) {
          if (Array.isArray(supabaseData.appointments)) {
            remoteAppointments = supabaseData.appointments;
          } else {
            console.warn('Dados de consultas remotos não são um array - ignorando');
          }
        }
        console.log(`Encontrados ${remoteAppointments.length} consultas no Supabase`);
        
        // Sessões
        if (supabaseData.sessions) {
          if (Array.isArray(supabaseData.sessions)) {
            remoteSessions = normalizeSessions(supabaseData.sessions);
          } else {
            console.warn('Dados de sessões remotos não são um array - ignorando');
          }
        }
        console.log(`Encontrados ${remoteSessions.length} sessões no Supabase`);
      } catch (e) {
        console.error('Erro ao analisar dados do Supabase:', e);
      }
      
      // Mesclar dados com tratamento de erro
      let mergedPatients: any[] = [], patientsChanged = false;
      let mergedAppointments: any[] = [], appointmentsChanged = false;
      let mergedSessions: any[] = [], sessionsChanged = false;
      
      try {
        const patientsResult = mergeLocalAndRemoteData(localPatients, remotePatients, 'patients');
        mergedPatients = patientsResult.merged;
        patientsChanged = patientsResult.changed;
      } catch (e) {
        console.error('Erro ao mesclar pacientes:', e);
        mergedPatients = [...localPatients];
      }
      
      try {
        const appointmentsResult = mergeLocalAndRemoteData(localAppointments, remoteAppointments, 'appointments');
        mergedAppointments = appointmentsResult.merged;
        appointmentsChanged = appointmentsResult.changed;
      } catch (e) {
        console.error('Erro ao mesclar consultas:', e);
        mergedAppointments = [...localAppointments];
      }
      
      try {
        const sessionsResult = mergeLocalAndRemoteData(localSessions, remoteSessions, 'sessions');
        mergedSessions = sessionsResult.merged;
        sessionsChanged = sessionsResult.changed;
      } catch (e) {
        console.error('Erro ao mesclar sessões:', e);
        mergedSessions = [...localSessions];
      }
      
      // Atualizar localStorage com segurança
      try {
        savePatients(mergedPatients);
        saveAppointments(mergedAppointments);
        // Converter array de sessões mescladas para o formato Record<string, any[]>
        const sessionsMap: Record<string, any[]> = {};
        mergedSessions.forEach((session: any) => {
          if (session && session.patientId) {
            if (!sessionsMap[session.patientId]) {
              sessionsMap[session.patientId] = [];
            }
            sessionsMap[session.patientId].push(session);
          }
        });
        saveSessions(sessionsMap);
        
        // Atualizar timestamp de sincronização
        safeSetItem(LAST_SYNC_TIME_KEY, remoteTimestamp.toString());
        
        console.log(`Dados mesclados e salvos no localStorage: ${mergedPatients.length} pacientes, ${mergedAppointments.length} consultas, ${mergedSessions.length} sessões`);
      } catch (e) {
        console.error('Erro ao salvar dados mesclados no localStorage:', e);
        return false;
      }
      
      // Se houve mudanças, atualizar o Supabase
      if (patientsChanged || appointmentsChanged || sessionsChanged) {
        console.log('Houve mudanças - atualizando Supabase');
        
        try {
          // Preparar pacote de dados para o Supabase
          const packagedData = {
            data: {
              patients: mergedPatients,
              appointments: mergedAppointments,
              sessions: mergedSessions
            },
            updated_at: normalizeTimestamp(new Date())
          };
          
          // Enviar dados atualizados para o Supabase
          const { error: updateError } = await supabase
            .from('user_data')
            .update(packagedData)
            .eq('user_id', userId);
          
          if (updateError) {
            console.error('Erro ao atualizar dados no Supabase:', updateError);
          } else {
            console.log('Dados atualizados no Supabase com sucesso');
          }
        } catch (e) {
          console.error('Erro ao atualizar Supabase após mesclagem:', e);
        }
      }
      
      // Emitir evento de sincronização
      try {
        const syncEvent = new CustomEvent('supabase-sync', { 
          detail: { 
            timestamp: Date.now(),
            source: 'load' 
          } 
        });
        window.dispatchEvent(syncEvent);
      } catch (e) {
        console.error('Erro ao emitir evento de sincronização:', e);
      }
      
      return true;
    } else {
      console.log('Dados locais são mais recentes ou iguais aos do Supabase');
      return false;
    }
  } catch (error) {
    console.error('Erro durante o carregamento do Supabase:', error);
    return false;
  }
}

/**
 * Mescla dados locais e remotos, preservando entradas únicas de ambas as fontes
 * e priorizando as versões mais recentes quando houver conflito
 */
function mergeLocalAndRemoteData(
  localData: any[], 
  remoteData: any[], 
  dataType: 'patients' | 'appointments' | 'sessions'
): { merged: any[]; changed: boolean } {
  console.log(`Iniciando mesclagem de ${dataType} - Local: ${localData?.length || 0}, Remoto: ${remoteData?.length || 0}`);
  
  // Garantir que estamos trabalhando com arrays
  const safeLocalData = Array.isArray(localData) ? localData : [];
  const safeRemoteData = Array.isArray(remoteData) ? remoteData : [];
  
  // Se não houver dados locais, retornar os dados remotos
  if (!safeLocalData.length && safeRemoteData.length) {
    console.log(`Sem dados locais, usando ${safeRemoteData.length} itens remotos`);
    const normalizedData = dataType === 'sessions' ? normalizeSessions(safeRemoteData) : safeRemoteData;
    return { merged: normalizedData, changed: true };
  }
  
  // Se não houver dados remotos, retornar os dados locais
  if (safeLocalData.length && !safeRemoteData.length) {
    console.log(`Sem dados remotos, mantendo ${safeLocalData.length} itens locais`);
    const normalizedData = dataType === 'sessions' ? normalizeSessions(safeLocalData) : safeLocalData;
    return { merged: normalizedData, changed: false };
  }
  
  // Se ambos estiverem vazios, retornar array vazio
  if (!safeLocalData.length && !safeRemoteData.length) {
    console.log(`Sem dados em ambas as fontes para ${dataType}`);
    return { merged: [], changed: false };
  }
  
  // Mapeamento de IDs para itens para facilitar a busca
  const localMap = new Map<string, any>();
  const remoteMap = new Map<string, any>();
  
  // Lista de itens marcados como excluídos
  const deletedItemIds = new Set<string>();
  
  // Preencher os mapas com segurança
  try {
    safeLocalData.forEach((item: any) => {
      if (item && item.id) {
        localMap.set(item.id, item);
        // Registra quais IDs estão marcados como excluídos localmente
        if (item.deleted) {
          deletedItemIds.add(item.id);
          console.log(`Item ${item.id} marcado como excluído localmente`);
        }
      }
    });
  } catch (e) {
    console.error(`Erro ao processar dados locais de ${dataType}:`, e);
  }
  
  try {
    safeRemoteData.forEach((item: any) => {
      if (item && item.id) {
        remoteMap.set(item.id, item);
        // Registra quais IDs estão marcados como excluídos remotamente
        if (item.deleted) {
          deletedItemIds.add(item.id);
          console.log(`Item ${item.id} marcado como excluído remotamente`);
        }
      }
    });
  } catch (e) {
    console.error(`Erro ao processar dados remotos de ${dataType}:`, e);
  }
  
  // Quando estamos lidando com agendamentos ou sessões,
  // precisamos verificar se o paciente foi excluído
  let excludedPatientIds = new Set<string>();
  if (dataType === 'appointments' || dataType === 'sessions') {
    try {
      const patients = loadPatients();
      
      if (Array.isArray(patients)) {
        // Coletar IDs de pacientes marcados como excluídos
        patients.forEach((patient: any) => {
          if (patient && patient.id && patient.deleted) {
            excludedPatientIds.add(patient.id);
          }
        });
        
        console.log(`Encontrados ${excludedPatientIds.size} pacientes excluídos para verificação`);
      }
    } catch (e) {
      console.error('Erro ao verificar pacientes excluídos:', e);
    }
  }
  
  let changed = false;
  const merged: any[] = [];
  
  // Processamos PRIMEIRO os itens excluídos para garantir que exclusões tenham prioridade
  if (deletedItemIds.size > 0) {
    console.log(`Processando ${deletedItemIds.size} itens excluídos primeiro`);
    
    deletedItemIds.forEach(id => {
      const localItem = localMap.get(id);
      const remoteItem = remoteMap.get(id);
      
      // Obter a versão mais recente do item excluído
      let newerDeletedItem = null;
      
      if (localItem && remoteItem) {
        // Se ambos existem, pegar o mais recente
        const localTimestamp = localItem.updated_at;
        const remoteTimestamp = remoteItem.updated_at;
        
        newerDeletedItem = compareTimestamps(localTimestamp, remoteTimestamp) >= 0 ? localItem : remoteItem;
      } else {
        // Se apenas um deles existe, usar esse
        newerDeletedItem = localItem || remoteItem;
      }
      
      if (newerDeletedItem) {
        // Garantir que está marcado como excluído (por segurança)
        newerDeletedItem = {
          ...newerDeletedItem,
          deleted: true,
          // Atualizar o timestamp para garantir que esta exclusão tenha prioridade
          updated_at: normalizeTimestamp(new Date())
        };
        
        // Adicionar à lista mesclada
        merged.push(newerDeletedItem);
        changed = true;
        
        // Remover dos mapas para não processá-los novamente
        localMap.delete(id);
        remoteMap.delete(id);
      }
    });
  }
  
  // Agora processar os itens relacionados a pacientes excluídos (para agendamentos/sessões)
  if ((dataType === 'appointments' || dataType === 'sessions') && excludedPatientIds.size > 0) {
    console.log(`Processando itens relacionados a pacientes excluídos`);
    
    // União de todas as chaves dos dois mapas
    const allIds = new Set<string>([
      ...Array.from(localMap.keys()), 
      ...Array.from(remoteMap.keys())
    ]);
    
    // Verificar cada item para ver se pertence a um paciente excluído
    allIds.forEach(id => {
      const localItem = localMap.get(id);
      const remoteItem = remoteMap.get(id);
      
      // Determinar o patientId
      const patientId = (localItem?.patientId || remoteItem?.patientId);
      
      if (patientId && excludedPatientIds.has(patientId)) {
        console.log(`Item ${id} pertence a paciente excluído (${patientId}), marcando como excluído`);
        
        // Usar o item mais recente como base
        let itemToModify = null;
        
        if (localItem && remoteItem) {
          const localTimestamp = localItem.updated_at;
        const remoteTimestamp = remoteItem.updated_at;
                          
          itemToModify = compareTimestamps(localTimestamp, remoteTimestamp) >= 0 ? localItem : remoteItem;
        } else {
          itemToModify = localItem || remoteItem;
        }
        
        if (itemToModify) {
          // Marcar como excluído e atualizar o timestamp
          const updatedItem = {
            ...itemToModify,
            deleted: true,
            updated_at: normalizeTimestamp(new Date())
          };
          
          // Adicionar à lista mesclada
          merged.push(updatedItem);
          changed = true;
          
          // Remover dos mapas para não processá-los novamente
          localMap.delete(id);
          remoteMap.delete(id);
        }
      }
    });
  }
  
  // Processar todos os IDs restantes (união de local e remoto)
  const remainingIds = new Set<string>([
    ...Array.from(localMap.keys()), 
    ...Array.from(remoteMap.keys())
  ]);
  
  console.log(`Processando ${remainingIds.size} itens restantes`);
  
  // Processar cada ID
  try {
    remainingIds.forEach(id => {
      const localItem = localMap.get(id);
      const remoteItem = remoteMap.get(id);
      
      // Se o item existe apenas localmente, adicioná-lo (nova adição local)
      if (localItem && !remoteItem) {
        console.log(`Item ${id} existe apenas localmente`);
        merged.push(localItem);
        changed = true;
        return;
      }
      
      // Se o item existe apenas remotamente, adicioná-lo (nova adição remota)
      if (!localItem && remoteItem) {
        console.log(`Item ${id} existe apenas remotamente`);
        merged.push(remoteItem);
        changed = true;
        return;
      }
      
      // Ambos existem, verificar qual é mais recente com base em updated_at
      if (localItem && remoteItem) {
        const localTimestamp = localItem.updated_at;
        const remoteTimestamp = remoteItem.updated_at;
        
        // Usar o item mais recente
        const comparison = compareTimestamps(localTimestamp, remoteTimestamp);
        if (comparison > 0) {
          console.log(`Item ${id} local é mais recente`);
          merged.push(localItem);
          changed = true;
        } else {
          console.log(`Item ${id} remoto é mais recente ou igual`);
          merged.push(remoteItem);
          if (comparison < 0) changed = true;
        }
      }
    });
  } catch (e) {
    console.error(`Erro durante a mesclagem de ${dataType}:`, e);
    // Em caso de erro, retornar a combinação segura
    return { 
      merged: [...safeLocalData, ...safeRemoteData.filter(rItem => 
        !safeLocalData.some(lItem => lItem.id === rItem.id)
      )],
      changed: true 
    };
  }
  
  console.log(`Mesclagem concluída - Resultados: ${merged.length} itens, Alterações: ${changed}`);
  
  // Verificação final para garantir que não haja duplicatas
  const uniqueMap = new Map<string, any>();
  merged.forEach(item => {
    if (item && item.id) {
      // Se já existe um item com esse ID, manter apenas o que NÃO está excluído
      // ou, se ambos estiverem excluídos ou não-excluídos, manter o mais recente
      if (uniqueMap.has(item.id)) {
        const existingItem = uniqueMap.get(item.id);
        
        // Se o existente está excluído e o novo não, substituir
        if (existingItem.deleted && !item.deleted) {
          uniqueMap.set(item.id, item);
        } 
        // Se o novo está excluído e o existente não, manter o existente
        else if (!existingItem.deleted && item.deleted) {
          // Não fazer nada, manter o existente
        }
        // Se ambos têm o mesmo status de exclusão, pegar o mais recente
        else {
          const existingTimestamp = existingItem.updated_at;
        const newTimestamp = item.updated_at;
                          
          if (compareTimestamps(newTimestamp, existingTimestamp) > 0) {
            uniqueMap.set(item.id, item);
          }
        }
      } else {
        // Se ainda não existe um item com esse ID, adicionar
        uniqueMap.set(item.id, item);
      }
    }
  });
  
  // Retornar a lista final de itens únicos
  const finalResult = Array.from(uniqueMap.values());
  console.log(`Após remoção de duplicatas: ${finalResult.length} itens únicos`);
  
  // Normalizar sessões se necessário
  const normalizedResult = dataType === 'sessions' ? normalizeSessions(finalResult) : finalResult;
  
  return { merged: normalizedResult, changed: changed };
}



/**
 * Configura sincronização em tempo real com o Supabase
 * @param userId ID do usuário logado
 * @returns Função para limpar os listeners
 */
export function setupRealtimeSync(userId: string): () => void {
  console.log(`Configurando sincronização em tempo real para usuário: ${userId}`);
  
  // NÃO realizar carregamento inicial automático para preservar dados locais
  // O carregamento será feito apenas quando necessário (manual ou por mudanças remotas)
  console.log('Sincronização em tempo real configurada sem carregamento inicial automático');
  
  // Criar sistema de verificação periódica mais moderada para compensar falhas no realtime
  const SYNC_CHECK_INTERVAL = 30000; // 30 segundos
  const fastCheckIntervalId = setInterval(async () => {
    console.log('Verificando dados atualizados no Supabase...');
    
    try {
      const { data, error } = await supabase
        .from('user_data')
        .select('updated_at')
        .eq('user_id', userId)
        .limit(1);
      
      if (error) {
        console.error('Erro ao verificar timestamp de atualização:', error);
        return;
      }
      
      if (!data || data.length === 0) {
        console.log('Nenhum dado encontrado para verificação de timestamp');
        return;
      }
      
      const userData = data[0];
      if (!userData || !userData.updated_at) return;
      
      const remoteTimestamp = new Date(userData.updated_at).getTime();
      const lastSyncTimeStr = safeGetItem(LAST_SYNC_TIME_KEY) || '0';
      const localTimestamp = parseInt(lastSyncTimeStr);
      
      console.log(`Comparando timestamps - Remoto: ${remoteTimestamp}, Local: ${localTimestamp}`);
      
      // Se o timestamp remoto for mais recente, forçar carregamento
      if (remoteTimestamp > localTimestamp) {
        console.log('Dados remotos são mais recentes, carregando atualizações...');
        
        const success = await loadFromSupabase(userId);
        if (success) {
          console.log('Dados atualizados com sucesso do servidor');
          
          // Disparar evento para notificar outros componentes
          const syncEvent = new CustomEvent('supabase-sync', { 
            detail: { 
              timestamp: Date.now(),
              source: 'polling-sync',
              userId
            } 
          });
          window.dispatchEvent(syncEvent);
        }
      }
    } catch (e) {
      console.error('Erro ao verificar atualizações:', e);
    }
  }, SYNC_CHECK_INTERVAL);
  
  // Verificação mais completa em intervalos maiores
  const FULL_SYNC_INTERVAL = 120000; // 2 minutos
  const fullSyncIntervalId = setInterval(async () => {
    console.log('Realizando sincronização completa...');
    const success = await loadFromSupabase(userId);
    
    if (success) {
      console.log('Sincronização completa concluída');
      
      // Disparar evento de sincronização
      const syncEvent = new CustomEvent('supabase-sync', { 
        detail: { 
          timestamp: Date.now(),
          source: 'full-sync',
          userId
        } 
      });
      window.dispatchEvent(syncEvent);
    }
  }, FULL_SYNC_INTERVAL);
  
  // Quando os dados locais são modificados, garantir que sejam enviados para o servidor
  const handleStorageChange = (event: StorageEvent | CustomEvent) => {
    let key: string | null = null;
    
    // Trata tanto eventos nativos de Storage quanto eventos customizados
    if (event instanceof StorageEvent) {
      key = event.key;
    } else if (event instanceof CustomEvent && event.detail) {
      key = event.detail.key;
    }
    
    if (!key) return;
    
    // Verifica se a alteração foi em alguma das chaves que nos interessam
    if (key === 'psicosys_patients' || key === 'psicosys_appointments' || key === 'psicosys_sessions') {
      console.log(`Detectada alteração em ${key}, sincronizando imediatamente...`);
      
      // Forçar sincronização imediata com o servidor
      saveToSupabase(userId).then(result => {
        if (result.success) {
          console.log('Dados locais sincronizados com sucesso para o servidor');
        } else {
          console.error('Falha ao sincronizar dados locais com o servidor:', result.message);
        }
      });
    }
  };
  
  // Adicionar listeners para mudanças no localStorage
  window.addEventListener('storage', handleStorageChange);
  window.addEventListener('storage', ((e: Event) => {
    handleStorageChange(e as unknown as CustomEvent);
  }) as EventListener);
  
  // Retornar função para limpar os listeners
  return () => {
    console.log('Encerrando sistema de sincronização');
    clearInterval(fastCheckIntervalId);
    clearInterval(fullSyncIntervalId);
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('storage', ((e: Event) => {
      handleStorageChange(e as unknown as CustomEvent);
    }) as EventListener);
  };
}

/**
 * Função para verificar se há conectividade com o Supabase
 * @returns Status da conexão
 */
export const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    // Tenta fazer uma operação simples no Supabase
    const { error } = await supabase.from('user_data').select('count', { count: 'exact' }).limit(1);
    
    if (error) {
      console.error('Erro ao verificar conexão com Supabase:', error);
      
      // Se o erro for 404, significa que a tabela não existe
      if (error.code === '404' || error.message.includes('404')) {
        console.log('A tabela user_data não existe. Tentaremos criá-la automaticamente.');
        return createUserDataTable();
      }
    }
    
    return !error;
  } catch (error) {
    console.error('Exceção ao verificar conexão com Supabase:', error);
    return false;
  }
};

/**
 * Tenta criar a tabela user_data
 */
async function createUserDataTable(): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('create_user_data_table');
    if (error) {
      console.error('Erro ao criar tabela user_data:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Exceção ao criar tabela user_data:', error);
    
    // Tentar criar direto com SQL como fallback
    try {
      const { error } = await supabase.rpc('execute_sql', {
        sql: `
        CREATE TABLE IF NOT EXISTS public.user_data (
          id BIGSERIAL PRIMARY KEY,
          user_id TEXT NOT NULL UNIQUE,
          data JSONB NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
        `
      });
      
      if (error) {
        console.error('Erro ao criar tabela via SQL:', error);
        return false;
      }
      
      return true;
    } catch (e) {
      console.error('Falha completa ao criar tabela:', e);
      return false;
    }
  }
}

/**
 * Cria um backup local dos dados no localStorage
 */
function createLocalBackup() {
  try {
    // Obter dados do localStorage
    // const patients = loadPatients();
    // const appointments = loadAppointments();
    // const sessions = loadSessions();
    const patients: any[] = [];
    const appointments: any[] = [];
    const sessions: any[] = [];
    
    // Criar chaves de backup com timestamp
    const timestamp = Date.now();
    const backupId = `${BACKUP_KEY_PREFIX}_${timestamp}`;
    
    // Salvar dados no backup
    if (patients && patients.length > 0) safeSetItem(`${backupId}_patients`, JSON.stringify(patients));
  if (appointments && appointments.length > 0) safeSetItem(`${backupId}_appointments`, JSON.stringify(appointments));
  if (sessions && sessions.length > 0) safeSetItem(`${backupId}_sessions`, JSON.stringify(sessions));
    
    // Atualizar timestamp do último backup
    safeSetItem(LAST_BACKUP_TIME_KEY, normalizeTimestamp(new Date()));
    
    // Limpar backups antigos (manter apenas os 3 mais recentes)
    cleanupOldBackups();
    
    console.log(`Backup local criado com sucesso: ${backupId}`);
    return true;
  } catch (error) {
    console.error('Erro ao criar backup local:', error);
    return false;
  }
}

/**
 * Limpa backups antigos mantendo apenas os 3 mais recentes
 */
function cleanupOldBackups() {
  try {
    // Obter todas as chaves do localStorage
    const allKeys = Object.keys(localStorage);
    
    // Filtrar apenas as chaves de backup
    const backupKeys = allKeys.filter(key => key.startsWith(BACKUP_KEY_PREFIX));
    
    // Agrupar backups por ID (timestamp)
    const backupGroups: Record<string, string[]> = {};
    backupKeys.forEach(key => {
      // Extrair o ID do backup (formato: backup_1234567890_patients)
      const parts = key.split('_');
      if (parts.length >= 3) {
        const backupId = `${parts[0]}_${parts[1]}`;
        if (!backupGroups[backupId]) {
          backupGroups[backupId] = [];
        }
        backupGroups[backupId].push(key);
      }
    });
    
    // Ordenar IDs de backup por timestamp (mais recentes primeiro)
    const backupIds = Object.keys(backupGroups).sort((a, b) => {
      const timestampA = parseInt(a.split('_')[1]);
      const timestampB = parseInt(b.split('_')[1]);
      return timestampB - timestampA;
    });
    
    // Manter apenas os 3 backups mais recentes
    if (backupIds.length > 3) {
      const backupsToRemove = backupIds.slice(3);
      backupsToRemove.forEach(backupId => {
        backupGroups[backupId].forEach(key => {
          safeRemoveItem(key);
        });
        console.log(`Backup antigo removido: ${backupId}`);
      });
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao limpar backups antigos:', error);
    return false;
  }
}

/**
 * Recupera dados de um backup específico
 * @param backupId ID do backup a ser restaurado
 */
function restoreFromBackup(backupId: string) {
  try {
    // Obter dados do backup
    const patients = safeGetItem(`${backupId}_patients`);
  const appointments = safeGetItem(`${backupId}_appointments`);
  const sessions = safeGetItem(`${backupId}_sessions`);
    
    // Restaurar dados para as chaves originais usando funções centralizadas
    if (patients) {
      try {
        JSON.parse(patients); // Validar JSON
        // savePatients(parsedPatients);
      } catch (e) {
        console.error('Erro ao restaurar pacientes do backup:', e);
      }
    }
    if (appointments) {
      try {
        JSON.parse(appointments); // Validar JSON
        // saveAppointments(parsedAppointments);
      } catch (e) {
        console.error('Erro ao restaurar agendamentos do backup:', e);
      }
    }
    if (sessions) {
      try {
        JSON.parse(sessions); // Validar JSON
        // saveSessions(parsedSessions);
      } catch (e) {
        console.error('Erro ao restaurar sessões do backup:', e);
      }
    }
    
    console.log(`Backup ${backupId} restaurado com sucesso`);
    return true;
  } catch (error) {
    console.error('Erro ao restaurar backup:', error);
    return false;
  }
}

/**
 * Lista todos os backups disponíveis
 * @returns Array de objetos com id e data de criação de cada backup
 */
export function listAvailableBackups() {
  try {
    // Obter todas as chaves do localStorage
    const allKeys = Object.keys(localStorage);
    
    // Filtrar apenas as chaves de backup
    const backupKeys = allKeys.filter(key => key.startsWith(BACKUP_KEY_PREFIX));
    
    // Extrair IDs únicos de backup
    const backupIds = new Set<string>();
    backupKeys.forEach(key => {
      const parts = key.split('_');
      if (parts.length >= 3) {
        const backupId = `${parts[0]}_${parts[1]}`;
        backupIds.add(backupId);
      }
    });
    
    // Converter para array de objetos com informações de data
    const backups = Array.from(backupIds).map(id => {
      const timestamp = parseInt(id.split('_')[1]);
      return {
        id,
        created_at: normalizeTimestamp(new Date(timestamp)),
        timestamp
      };
    });
    
    // Ordenar por data (mais recentes primeiro)
    return backups.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Erro ao listar backups disponíveis:', error);
    return [];
  }
}

// Função restoreFromLocalBackupIfNeeded removida - não estava sendo utilizada

/**
 * Força uma sincronização completa entre os dados locais e o servidor
 * Útil para resolver conflitos de dados entre dispositivos
 */
export async function forceFullSync(userId: string): Promise<{success: boolean, message: string}> {
  console.log('Iniciando sincronização forçada completa...');
  
  try {
    // Primeiro, criar um backup do estado atual
    createLocalBackup();
    console.log('Backup de segurança criado antes da sincronização forçada');
    
    // Buscar dados do servidor
    const { data, error } = await supabase
      .from('user_data')
      .select('*')
      .eq('user_id', userId)
      .limit(1);
    
    if (error) {
      console.error('Erro ao buscar dados do servidor para sincronização forçada:', error);
      return { 
        success: false, 
        message: 'Erro ao buscar dados do servidor: ' + error.message 
      };
    }
    
    if (!data || data.length === 0) {
      console.error('Nenhum dado encontrado no servidor para este usuário');
      return { 
        success: false, 
        message: 'Nenhum dado encontrado no servidor' 
      };
    }
    
    // Extrair dados do servidor
    const userData = data[0];
    
    if (!userData.data) {
      console.error('Dados do servidor estão vazios ou mal formatados');
      return { 
        success: false, 
        message: 'Dados do servidor estão vazios ou mal formatados' 
      };
    }
    
    // Verificar se temos dados locais que precisam ser mesclados com o servidor
    // Recuperar os dados do localStorage com tratamento seguro
    let localPatients: any[] = [];
    let localAppointments: any[] = [];
    let localSessions: any[] = [];
    
    try {
      // localPatients = loadPatients();
      if (!Array.isArray(localPatients)) {
        console.warn('Dados de pacientes locais não são um array - corrigindo');
        localPatients = [];
      }
    } catch (e: any) {
      console.error('Erro ao processar pacientes locais:', e);
      localPatients = [];
    }
    
    try {
      // localAppointments = loadAppointments();
      if (!Array.isArray(localAppointments)) {
        console.warn('Dados de consultas locais não são um array - corrigindo');
        localAppointments = [];
      }
    } catch (e: any) {
      console.error('Erro ao processar consultas locais:', e);
      localAppointments = [];
    }
    
    try {
      // localSessions = loadSessions();
      if (!Array.isArray(localSessions)) {
        console.warn('Dados de sessões locais não são um array - convertendo');
        // Se estiver no formato antigo (objeto agrupado por paciente), converter para array
        if (typeof localSessions === 'object') {
          localSessions = Object.values(localSessions).flat();
        } else {
          localSessions = [];
        }
        // Atualizar usando função centralizada com o formato correto
        // saveSessions(localSessions);
      }
    } catch (e) {
      console.error('Erro ao processar sessões locais:', e);
      localSessions = [];
    }
    
    // Garantir que temos arrays válidos do servidor
    let remotePatients = [];
    let remoteAppointments = [];
    let remoteSessions = [];
    
    try {
      // Verificar se os dados estão no formato correto
      const supabaseData = userData.data || {};
      
      // Pacientes
      if (supabaseData.patients) {
        if (Array.isArray(supabaseData.patients)) {
          remotePatients = supabaseData.patients;
        } else {
          console.warn('Dados de pacientes remotos não são um array - ignorando');
        }
      }
      console.log(`Encontrados ${remotePatients.length} pacientes no Supabase`);
      
      // Consultas
      if (supabaseData.appointments) {
        if (Array.isArray(supabaseData.appointments)) {
          remoteAppointments = supabaseData.appointments;
        } else {
          console.warn('Dados de consultas remotos não são um array - ignorando');
        }
      }
      console.log(`Encontrados ${remoteAppointments.length} consultas no Supabase`);
      
      // Sessões
      if (supabaseData.sessions) {
        if (Array.isArray(supabaseData.sessions)) {
          remoteSessions = supabaseData.sessions;
        } else {
          console.warn('Dados de sessões remotos não são um array - ignorando');
        }
      }
      console.log(`Encontrados ${remoteSessions.length} sessões no Supabase`);
    } catch (e: any) {
      console.error('Erro ao analisar dados do Supabase:', e);
      return { 
        success: false, 
        message: 'Erro ao analisar dados do servidor: ' + e.message 
      };
    }
    
    // Mesclar dados usando nossa função avançada de mesclagem
    try {
      const patientsResult = mergeLocalAndRemoteData(localPatients, remotePatients, 'patients');
      const mergedPatients = patientsResult.merged;
      
      const appointmentsResult = mergeLocalAndRemoteData(localAppointments, remoteAppointments, 'appointments');
      const mergedAppointments = appointmentsResult.merged;
      
      const sessionsResult = mergeLocalAndRemoteData(localSessions, remoteSessions, 'sessions');
      const mergedSessions = sessionsResult.merged;
      
      // Atualizar localStorage com os dados mesclados
      // savePatients(mergedPatients);
      // saveAppointments(mergedAppointments);
      // saveSessions(mergedSessions);
      
      // Atualizar timestamp de sincronização
      const timestamp = Date.now();
      safeSetItem(LAST_SYNC_TIME_KEY, timestamp.toString());
      
      console.log(`Dados mesclados e salvos no localStorage com sucesso`);
      console.log(`- Pacientes: ${mergedPatients.length} (ativos: ${mergedPatients.filter(p => !p.deleted).length})`);
      console.log(`- Consultas: ${mergedAppointments.length} (ativas: ${mergedAppointments.filter(a => !a.deleted).length})`);
      console.log(`- Sessões: ${mergedSessions.length} (ativas: ${mergedSessions.filter(s => !s.deleted).length})`);
      
      // Salvar os dados atualizados de volta no Supabase
      console.log('Enviando dados mesclados de volta para o Supabase...');
      const saveResult = await saveToSupabase(userId);
      
      if (!saveResult.success) {
        console.error('Falha ao salvar dados mesclados no Supabase:', saveResult.message);
        return {
          success: false,
          message: 'Erro ao salvar dados mesclados: ' + saveResult.message
        };
      }
      
      console.log('Dados atualizados no Supabase com sucesso após sincronização forçada');
      
      // Disparar evento para notificar outros componentes
      const syncEvent = new CustomEvent('supabase-sync', { 
        detail: { 
          timestamp: Date.now(),
          source: 'forced-sync',
          userId: userId
        } 
      });
      window.dispatchEvent(syncEvent);
      
      return { 
        success: true, 
        message: 'Sincronização forçada concluída com sucesso' 
      };
    } catch (e: any) {
      console.error('Erro durante a sincronização forçada:', e);
      return { 
        success: false, 
        message: 'Erro durante a sincronização: ' + e.message 
      };
    }
  } catch (error: any) {
    console.error('Erro geral na sincronização forçada:', error);
    return { 
      success: false, 
      message: 'Erro geral na sincronização: ' + (error.message || 'Erro desconhecido') 
    };
  }
}

/**
 * Integração com o Supabase e funcionalidades de sincronização
 */
export const supabaseService = {
  /**
   * Salva os dados do localStorage para o Supabase
   */
  saveToSupabase: async () => {
    const userId = getCurrentUserId();
    return saveToSupabase(userId);
  },
  
  /**
   * Carrega os dados do Supabase para o localStorage
   */  
  loadFromSupabase: async () => {
    const userId = getCurrentUserId();
    return loadFromSupabase(userId);
  },
  
  /**
   * Cria um backup local dos dados
   */
  createBackup: createLocalBackup,
  
  /**
   * Restaura dados do backup local se necessário
   */
  restoreFromBackup: restoreFromBackup,
  
  /**
   * Verifica se existem dados locais
   */
  checkLocalData,
  
  /**
   * Retorna o cliente Supabase para uso direto
   */
  getClient: () => supabase,
  
  /**
   * Obtém o ID do usuário atual
   */
  getUserId: () => getCurrentUserId(),
  
  /**
   * Configura a sincronização em tempo real
   */
  setupRealtimeSync: () => {
    const userId = getCurrentUserId();
    return setupRealtimeSync(userId);
  },
  
  /**
   * Força uma sincronização imediata
   */
  forceSyncNow: async () => {
    const userId = getCurrentUserId();
    // Usa o novo método para forçar download
    return forceFullSync(userId);
  },
  
  /**
   * Verifica status da sincronização
   */
  getSyncStatus: () => {
    return { inProgress: syncInProgress };
  },
  
  /**
   * Lista todos os backups disponíveis
   */
  listAvailableBackups: listAvailableBackups,
  
  /**
   * Obtém o timestamp da última sincronização
   */
  getLastSyncTime: () => {
    return safeGetItem(LAST_SYNC_TIME_KEY);
  },
  
  /**
   * Define o timestamp da última sincronização
   */
  setLastSyncTime: (timestamp: string) => {
    safeSetItem(LAST_SYNC_TIME_KEY, timestamp);
  }
};